import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { PageTitle } from '~/components/common';
import {
  CollectingRequestFilter,
  CollectingRequestRegisterModal,
  CollectingRequestTable,
} from '~/components/delivery-agency';
import { PageWrapper } from '~/components/layout';

import { useModal, useToggle } from '~/hooks/common';
import {
  deliveryAgencyKeys,
  useGetCollectingRequestsQuery,
  useGetCollectingRequestsSearchConditionsQuery,
  usePostCollectingRequestsCancelMutation,
} from '~/hooks/delivery-agency/queries';
import { addIf } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { OPTION_ALL, TOAST_MESSAGE } from '~/constants';
import { BREAD_CRUMB } from '~/constants/breadcrumb';
import {
  COLLECTING_REQUEST_CANCEL_MESSAGE,
  COLLECTING_REQUEST_LINK_PARAM,
  COLLECTING_REQUEST_OUTBOUND_KEYWORD_TYPE,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  MAX_DELIVERY_AGENCY_DATE_RANGE,
} from '~/constants/delivery-agency';
import { DELIVERY_AGENCY } from '~/constants/errorCode';
import { PATH } from '~/constants/path';

import type {
  CollectingRequestFilterOptions,
  CollectingRequestFilterValues,
  CollectingRequestFilterValuesStatus,
  CollectingRequestListItem,
  GetCollectingRequestsParams,
} from '~/types/delivery-agency.types';

const EMPTY_FILTER_OPTIONS: CollectingRequestFilterOptions = {
  searchKeywordTypes: [],
  collectingRequestStatuses: [],
  collectedProductHandlings: [],
};

const getDefaultCollectingRequestedDateRange = () => ({
  start: dayjs().startOf('day').toDate(),
  end: dayjs().startOf('day').toDate(),
});

const INITIAL_SEARCH_FILTER_VALUES: CollectingRequestFilterValues = {
  keywordType: null,
  keyword: '',
  collectingRequestedDateRange: getDefaultCollectingRequestedDateRange(),
  collectingRequestStatuses: [OPTION_ALL],
  collectedProductHandling: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
    totalCount: 0,
  },
};

const INITIAL_SEARCH_FILTER_VALUES_STATUS: CollectingRequestFilterValuesStatus = {
  keyword: 'default',
  collectingRequestedDateRange: 'default',
};

const DeliveryAgencyCollectingRequestPage = () => {
  const queryClient = useQueryClient();
  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });
  const registerModal = useModal();

  const [searchFilterValues, setSearchFilterValues] = useState(INITIAL_SEARCH_FILTER_VALUES);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState(INITIAL_SEARCH_FILTER_VALUES_STATUS);
  const [collectingRequestsQueryParams, setCollectingRequestsQueryParams] =
    useState<GetCollectingRequestsParams | null>(null);
  const [selectedRows, setSelectedRows] = useState<CollectingRequestListItem[]>([]);
  const [searchParams] = useSearchParams();
  const hasAppliedLinkParams = useRef(false);

  const searchConditionsQuery = useGetCollectingRequestsSearchConditionsQuery({ showGlobalErrorToast: false });
  const collectingRequestsQuery = useGetCollectingRequestsQuery({
    params: collectingRequestsQueryParams,
    showGlobalDimOverlay: false,
  });
  const cancelMutation = usePostCollectingRequestsCancelMutation();

  const filterOptions = searchConditionsQuery.data ?? EMPTY_FILTER_OPTIONS;

  // 키워드 구분은 고르기 전까지 검색조건의 첫 옵션(반품요청번호)을 기본값으로 쓴다
  const resolvedFilterValues = {
    ...searchFilterValues,
    keywordType: searchFilterValues.keywordType ?? filterOptions.searchKeywordTypes[0] ?? null,
  };

  const pagination = {
    currentPage: collectingRequestsQuery.data?.pagination.cursor ?? searchFilterValues.pagination.currentPage,
    pageSize: collectingRequestsQuery.data?.pagination.listView ?? searchFilterValues.pagination.pageSize,
    totalCount: collectingRequestsQuery.data?.pagination.listTotal ?? 0,
  };

  const buildListParams = (values: CollectingRequestFilterValues): GetCollectingRequestsParams | null => {
    if (!values.collectingRequestedDateRange) return null;

    const { start, end } = values.collectingRequestedDateRange;
    const statuses = values.collectingRequestStatuses
      .filter((status) => status.value !== OPTION_ALL.value)
      .map((status) => status.value);

    return {
      collectingRequestedDateFrom: formatDate(start, 'YYYY-MM-DD') ?? '',
      collectingRequestedDateTo: formatDate(end, 'YYYY-MM-DD') ?? '',
      ...addIf(values.keywordType?.value && values.keyword, 'searchKeywordType', values.keywordType?.value),
      ...addIf(values.keywordType?.value && values.keyword, 'searchKeyword', values.keyword),
      ...addIf(statuses.length > 0, 'collectingRequestStatuses', statuses.join(',')),
      ...addIf(
        values.collectedProductHandling.value !== OPTION_ALL.value,
        'collectedProductHandling',
        values.collectedProductHandling.value
      ),
      cursor: values.pagination.currentPage,
      limit: values.pagination.pageSize,
    } as GetCollectingRequestsParams;
  };

  const handleSearch = (values: CollectingRequestFilterValues) => {
    if (!values.collectingRequestedDateRange) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, collectingRequestedDateRange: 'error' }));
      dialogService.addDialog({ type: 'alert', children: '반품접수일을 입력 후 검색해 주세요.' });
      return;
    }

    const { start, end } = values.collectingRequestedDateRange;
    if (dayjs(end).diff(dayjs(start), 'day') + 1 > MAX_DELIVERY_AGENCY_DATE_RANGE) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, collectingRequestedDateRange: 'error' }));
      dialogService.addDialog({ type: 'alert', children: '최대 31일로 입력 후 검색해주세요.' });
      return;
    }

    const params = buildListParams(values);
    if (!params) return;

    setSearchFilterValues(values);
    setSearchFilterValuesStatus(INITIAL_SEARCH_FILTER_VALUES_STATUS);
    setSelectedRows([]);
    queryClient.invalidateQueries({ queryKey: deliveryAgencyKeys.collectingRequests(params) });
    setCollectingRequestsQueryParams(params);
  };

  /**
   * 배송 주문 상세의 `회수 내역`으로 진입하면 출고번호와 반품접수일을 받아 바로 검색한다.
   * 검색 키워드 타입 옵션이 도착한 뒤에 한 번만 실행한다.
   */
  useEffect(() => {
    if (hasAppliedLinkParams.current || filterOptions.searchKeywordTypes.length === 0) return;

    const outboundCode = searchParams.get(COLLECTING_REQUEST_LINK_PARAM.OUTBOUND_CODE);
    const dateFrom = searchParams.get(COLLECTING_REQUEST_LINK_PARAM.DATE_FROM);
    const dateTo = searchParams.get(COLLECTING_REQUEST_LINK_PARAM.DATE_TO);
    const keywordType = filterOptions.searchKeywordTypes.find(
      (option) => option.value === COLLECTING_REQUEST_OUTBOUND_KEYWORD_TYPE
    );

    if (!outboundCode || !dateFrom || !dateTo || !keywordType) return;

    hasAppliedLinkParams.current = true;
    handleSearch({
      ...INITIAL_SEARCH_FILTER_VALUES,
      keywordType,
      keyword: outboundCode,
      collectingRequestedDateRange: { start: dayjs(dateFrom).toDate(), end: dayjs(dateTo).toDate() },
    });
  }, [filterOptions, searchParams]);

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      collectingRequestedDateRange: getDefaultCollectingRequestedDateRange(),
    });
    setSearchFilterValuesStatus(INITIAL_SEARCH_FILTER_VALUES_STATUS);
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...resolvedFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  /** 검색에 적용된 params 기준으로 목록을 다시 불러온다. 필터 편집 중인 값은 반영하지 않는다 */
  const handleRefreshList = () => {
    setSelectedRows([]);
    queryClient.invalidateQueries({ queryKey: deliveryAgencyKeys.collectingRequests(collectingRequestsQueryParams) });
  };

  const handleClickCancel = () => {
    dialogService.addDialog({
      type: 'confirm',
      title: `선택한 반품요청번호 ${selectedRows.length}개를 반품 취소하시겠습니까?`,
      children: '취소 후 상태를 다시 되돌릴 수 없습니다.',
      onConfirm: () => {
        cancelMutation.mutate(
          { collectingRequestCodes: selectedRows.map((row) => row.collectingRequestCode) },
          {
            onSuccess: ({ totalCount, successCount, failureCount }) => {
              if (failureCount === 0) {
                toastService.addToast({ message: '반품 취소되었습니다.' });
              } else if (successCount === 0) {
                dialogService.addDialog({ children: COLLECTING_REQUEST_CANCEL_MESSAGE.ALL_FAILED(totalCount) });
              } else {
                dialogService.addDialog({ children: COLLECTING_REQUEST_CANCEL_MESSAGE.PARTIAL_FAILED(successCount) });
              }
              handleRefreshList();
            },
            onError: (error) => {
              if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
                dialogService.addDialog({ children: error?.message });
                return;
              }
              toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
            },
          }
        );
      },
    });
  };

  const handleChangePage = (page: number) => {
    handleSearch({
      ...resolvedFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: page, pageSize: pagination.pageSize },
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="배송 주문 반품 신청/조회"
        breadcrumbs={BREAD_CRUMB[PATH.CLAIM_COLLECTING]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <CollectingRequestFilter
          options={filterOptions}
          values={resolvedFilterValues}
          valuesStatus={searchFilterValuesStatus}
          onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
          onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
          onSearch={() =>
            handleSearch({
              ...resolvedFilterValues,
              pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
            })
          }
          onReset={handleResetFilterValues}
        />
      )}
      <Divider color="gray100" />
      <CollectingRequestTable
        data={collectingRequestsQuery.data?.data ?? null}
        pagination={pagination}
        selectedRows={selectedRows}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onChangeRowSelection={setSelectedRows}
        onClickRegister={registerModal.handleOpenModal}
        onClickCancel={handleClickCancel}
      />
      {registerModal.isOpen && (
        <CollectingRequestRegisterModal
          open={registerModal.isOpen}
          onClose={registerModal.handleCloseModal}
          onRegistered={handleRefreshList}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyCollectingRequestPage;
